import { useBlocker } from "@tanstack/react-router";
import { useCallback, useEffect, useRef } from "react";

const defaultMessage = "You have unsaved changes - are you sure you wish to leave this page?";

type UseNavigationPromptArgs = {
  enable: boolean;
  message?: string;
  allowPath?: (path: string) => boolean;
  onConfirm?: () => void;
};

export default function useNavigationPrompt({
  enable,
  message = defaultMessage,
  allowPath,
  onConfirm,
}: UseNavigationPromptArgs): { bypassExitConfirmation: (value?: boolean) => void } {
  const bypassConfirmationRef = useRef(false);

  const shouldBlockFn = useCallback(
    ({ next }: { next: { pathname: string } }) => {
      if (!enable || bypassConfirmationRef.current) {
        return false;
      }
      if (allowPath?.(next.pathname)) {
        return false;
      }
      if (window.confirm(message)) {
        onConfirm?.();
        return false;
      }
      return true;
    },
    [enable, message, allowPath, onConfirm],
  );

  useBlocker({
    shouldBlockFn,
    enableBeforeUnload: enable,
    disabled: !enable,
  });

  // Handle browser close/refresh with custom message
  useEffect(() => {
    const handleWindowClose = (e: BeforeUnloadEvent) => {
      if (!enable || bypassConfirmationRef.current) {
        return undefined;
      }
      e.preventDefault();
      return (e.returnValue = message);
    };
    window.addEventListener("beforeunload", handleWindowClose);
    return () => window.removeEventListener("beforeunload", handleWindowClose);
  }, [enable, message]);

  return {
    bypassExitConfirmation(value = true) {
      bypassConfirmationRef.current = value;
    },
  };
}
